import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Pencil, Sliders, Trash2 } from 'lucide-react';

import { cn } from '../../lib/cn';
import { colorForClassIndex } from '../../utils/classColors';
import { imagesApi } from '../../services/annotatorApi';
import type { BoundingBox, Class as ClassModel, ImageMeta } from '../../types/api';

/**
 * BoxesPanel — "My Canvas" view of the right rail.
 *
 * Row interactions:
 *   • Click row              → SELECT only (highlight on canvas, stay on My
 *                              Canvas — no auto-switch to Properties)
 *   • Click Sliders icon     → SELECT + switch to Box Properties view
 *   • Click ↑ / ↓ icons      → one-step forward / backward (stopPropagation)
 *   • Click 🗑 icon          → delete (stopPropagation)
 *   • Right-click anywhere   → SELECT for canvas highlight + context menu
 *                              (no view change — context menu has its own
 *                              'Open in Box Properties' item for that)
 */
interface BoxesPanelProps {
    isDarkMode?: boolean;
    boxes: BoundingBox[];
    classes: ClassModel[];
    selectedBoxId: string | null;
    hiddenClassIndices: Set<number>;
    naturalSize?: { w: number; h: number } | null;
    activeImage: ImageMeta | null;
    orgId?: string;
    drawPriorityMode: boolean;
    /** When true (project is locked), the pencil rename button is
     *  hidden — rename is a mutation, gated by the same lock that
     *  blocks box edits. */
    isLocked?: boolean;
    onSelectBoxOnly: (id: string) => void;
    onOpenBoxInProperties: (id: string) => void;
    onDeleteBox: (id: string) => void;
    onToggleDrawPriority: () => void;
    /** Optional — when provided AND project unlocked, the pencil
     *  icon in the header row becomes a separate clickable button
     *  that opens the RenameImageModal. */
    onRenameImage?: (image: ImageMeta) => void;
    onBringBoxForward: (id: string) => void;
    onSendBoxBackward: (id: string) => void;
    onContextMenuBox: (id: string, x: number, y: number) => void;
}

export default function BoxesPanel({
    isDarkMode = false,
    boxes,
    classes,
    selectedBoxId,
    hiddenClassIndices,
    naturalSize,
    activeImage,
    orgId,
    drawPriorityMode,
    isLocked = false,
    onSelectBoxOnly,
    onOpenBoxInProperties,
    onDeleteBox,
    onToggleDrawPriority,
    onRenameImage,
    onBringBoxForward,
    onSendBoxBackward,
    onContextMenuBox,
}: BoxesPanelProps) {
    const { t } = useTranslation('canvas');
    const [hoveredId, setHoveredId] = useState<string | null>(null);

    // `boxes` is in RENDER order (lowest z first → frontmost last). For the
    // list panel we mirror Photoshop/Figma/Sketch: frontmost at the TOP of
    // the list. So reverse before rendering. The ▲ ArrowUp button then
    // correctly maps to "move up in this list" = bringForward in z-stack.
    const visibleBoxes = useMemo(
        () =>
            boxes
                .filter((b) => !hiddenClassIndices.has(b.class_index))
                .slice()
                .reverse(),
        [boxes, hiddenClassIndices],
    );

    const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
    const thumbUrl = activeImage ? imagesApi.thumbnailUrl(activeImage.id, orgId) : null;

    return (
        <section className={cn('flex flex-col h-full min-h-0', isDarkMode ? 'bg-gray-900' : 'bg-white')}>
            {/* Compact header — the row is the draw-priority toggle
                (click thumbnail/filename/anywhere outside the pencil
                button → flips draw-on-top mode). The pencil at the
                right is a SEPARATE button: when the project is
                unlocked, it opens the rename modal. stopPropagation
                so it doesn't also flip draw-priority. Refactored from
                a wrapping <button> to a div-with-role so the inner
                pencil button is valid HTML (no nested buttons). */}
            {activeImage && (
                <div
                    role="button"
                    tabIndex={0}
                    onClick={onToggleDrawPriority}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onToggleDrawPriority();
                        }
                    }}
                    aria-pressed={drawPriorityMode}
                    title={t('draw_priority_hint', { defaultValue: 'Click to draw new boxes anywhere (including over existing ones)' })}
                    className={cn(
                        'w-full px-3 py-2 border-b flex items-center gap-2 text-left transition-colors cursor-pointer',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset',
                        isDarkMode ? 'border-gray-700' : 'border-gray-200',
                        drawPriorityMode
                            ? isDarkMode ? 'bg-primary-500/10' : 'bg-primary-50/60'
                            : isDarkMode ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50',
                    )}
                >
                    {thumbUrl ? (
                        <span className={cn(
                            'h-10 w-10 rounded-md overflow-hidden border-2 shrink-0 transition-all duration-150',
                            drawPriorityMode
                                ? isDarkMode ? 'border-primary-400 ring-1 ring-primary-500/30' : 'border-primary-500 ring-1 ring-primary-500/30'
                                : isDarkMode ? 'border-gray-700' : 'border-gray-200',
                        )}>
                            <img src={thumbUrl} alt="" className="h-full w-full object-cover" draggable={false} />
                        </span>
                    ) : null}
                    <div className="flex-1 min-w-0">
                        <p className={cn('text-[12px] font-medium truncate', isDarkMode ? 'text-gray-200' : 'text-gray-800')} title={activeImage.filename}>
                            {activeImage.filename}
                        </p>
                        <p className={cn('text-[10px] truncate', muted)}>
                            {drawPriorityMode
                                ? t('draw_priority_active', { defaultValue: 'Drawing on top — click anywhere' })
                                : t('draw_freely_hint_row', { defaultValue: 'Click row to draw on top' })}
                        </p>
                    </div>
                    {!isLocked && onRenameImage ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation(); // don't toggle draw-priority
                                onRenameImage(activeImage);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            title={t('rename_image', { defaultValue: 'Rename image' })}
                            aria-label={t('rename_image', { defaultValue: 'Rename image' })}
                            className={cn(
                                'shrink-0 p-1.5 rounded-md transition-colors',
                                isDarkMode
                                    ? 'text-gray-400 hover:bg-gray-700 hover:text-primary-300'
                                    : 'text-gray-500 hover:bg-gray-200 hover:text-primary-700',
                            )}
                        >
                            <Pencil className="h-4 w-4" />
                        </button>
                    ) : (
                        // Locked or no handler — pencil stays as a
                        // visual indicator of draw-priority state.
                        <span
                            className={cn(
                                'shrink-0 p-1.5 rounded-md',
                                drawPriorityMode
                                    ? isDarkMode ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-100 text-primary-700'
                                    : isDarkMode ? 'text-gray-400' : 'text-gray-500',
                            )}
                        >
                            <Pencil className="h-4 w-4" />
                        </span>
                    )}
                </div>
            )}

            <div className="px-2 py-1.5 flex-1 min-h-0 overflow-y-auto">
                {visibleBoxes.length === 0 ? (
                    <p className={cn('text-xs py-3 px-2 italic text-center', muted)}>
                        {boxes.length === 0
                            ? t('no_boxes_yet', { defaultValue: 'No boxes yet — draw one on the canvas.' })
                            : t('all_boxes_hidden', { defaultValue: 'All boxes hidden via class visibility toggle.' })}
                    </p>
                ) : (
                    <ul className="space-y-0.5">
                        {visibleBoxes.map((b) => {
                            const cls = classes.find((c) => c.class_index === b.class_index);
                            const color = cls?.color || colorForClassIndex(b.class_index);
                            const name = cls?.name ?? `class ${b.class_index}`;
                            const isSelected = b.id === selectedBoxId;
                            const isHovered = hoveredId === b.id;
                            const shortId = b.id.split('-').pop()?.slice(0, 4) ?? b.id.slice(0, 4);
                            const xPct = (b.x_center * 100).toFixed(0);
                            const yPct = (b.y_center * 100).toFixed(0);
                            const sizeStr = naturalSize
                                ? `${Math.round(b.width * naturalSize.w)}×${Math.round(b.height * naturalSize.h)}`
                                : `${(b.width * 100).toFixed(0)}×${(b.height * 100).toFixed(0)}%`;

                            const actionBtnCls = cn(
                                'p-0.5 rounded shrink-0 transition-colors',
                                isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600',
                            );

                            return (
                                <li
                                    key={b.id}
                                    onMouseEnter={() => setHoveredId(b.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                >
                                    <div
                                        onClick={() => onSelectBoxOnly(b.id)}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            onContextMenuBox(b.id, e.clientX, e.clientY);
                                        }}
                                        className={cn(
                                            'pl-1.5 pr-1.5 py-1 rounded-md cursor-pointer text-[12px] transition-colors',
                                            isSelected
                                                ? isDarkMode
                                                    ? 'bg-primary-500/15 border-l-2 border-primary-400 text-gray-100'
                                                    : 'bg-primary-50 border-l-2 border-primary-500 text-gray-900'
                                                : isDarkMode
                                                    ? 'border-l-2 border-transparent text-gray-300 hover:bg-gray-800/50'
                                                    : 'border-l-2 border-transparent text-gray-700 hover:bg-gray-50',
                                        )}
                                        title={t('row_click_hint', { defaultValue: 'Click to highlight on canvas · Sliders icon to edit · right-click for more' })}
                                    >
                                        {/* Top line: color + label/class + short ID */}
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                                style={{ backgroundColor: color }}
                                                aria-hidden="true"
                                            />
                                            <span className="truncate font-medium flex-1">
                                                {b.label ? b.label : name}
                                                {b.label && <span className={cn('ml-1.5 text-[10px] font-normal', muted)}>· {name}</span>}
                                            </span>
                                            <span
                                                className={cn(
                                                    'text-[10px] font-mono tabular-nums shrink-0 px-1 rounded',
                                                    isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-500',
                                                )}
                                                title={t('box_short_id_hint', { defaultValue: 'Auto-ID (last 4 of UUID): {{id}}', id: b.id })}
                                            >
                                                #{shortId}
                                            </span>
                                        </div>
                                        {/* Bottom line: position summary + action buttons */}
                                        <div className="flex items-center gap-1.5 mt-0.5 ml-4">
                                            <span className={cn('text-[10px] tabular-nums truncate flex-1', muted)}>
                                                {xPct}%,{yPct}% · {sizeStr}
                                            </span>
                                            {(isHovered || isSelected) && (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onBringBoxForward(b.id);
                                                        }}
                                                        title={t('bring_forward', { defaultValue: 'Move one step forward (right-click → Bring to Front)' })}
                                                        className={actionBtnCls}
                                                    >
                                                        <ArrowUp className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSendBoxBackward(b.id);
                                                        }}
                                                        title={t('send_backward', { defaultValue: 'Move one step backward (right-click → Send to Back)' })}
                                                        className={actionBtnCls}
                                                    >
                                                        <ArrowDown className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onOpenBoxInProperties(b.id);
                                                        }}
                                                        title={t('open_in_properties', { defaultValue: 'Open Properties' })}
                                                        className={actionBtnCls}
                                                    >
                                                        <Sliders className="h-3 w-3" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDeleteBox(b.id);
                                                        }}
                                                        title={t('delete_box', { defaultValue: 'Delete box' })}
                                                        className={cn(
                                                            'p-0.5 rounded shrink-0 transition-colors',
                                                            isDarkMode ? 'hover:bg-red-900/30 text-red-400' : 'hover:bg-red-50 text-red-500',
                                                        )}
                                                    >
                                                        <Trash2 className="h-3 w-3" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </section>
    );
}
